import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { api } from "../../lib/api";
import { wabaApi } from "../../lib/wabaApi";
import { useUnsavedGuard } from "./unsavedGuard";
import { SavedNote } from "../shared/Icons";
import { ExpressionEditor } from "../common/LazyExpressionEditor";
import { bodyOf, countSlots } from "../../lib/wabaTemplates";
import { ExecModal, type ExecRow } from "../shared/ExecModal";
import { toastError } from "../shared/toast";
import { DialogPanel } from "../shared/DialogPanel";
import { backdropClose } from "../shared/backdrop";

// Portais: leads que os portais (iCarros, Mobiauto, Só Carrão…) mandam POR E-MAIL pro
// endereço da WABA vinculada (card da conexão → E-mail). O core extrai o comprador
// (LLM configurado em Dev → Portais por e-mail), renderiza o template daqui com as
// variáveis portais.* e a WABA cria o lead E dispara na mesma chamada (cooldown aqui).
// Mini dash: últimos e-mails, leads extraídos, badge do disparo (status da Meta lido do
// log) e Enviar/Reenviar com o template ATUAL (30 dias).

type Tpl = { name: string; language: string; params: string[] };
type Sender = { name: string; match: string };
type Dispatch = {
  status: "sent" | "failed" | "skipped_conversa_ativa" | "skipped_timeout" | "skipped_integracao_olx" | "skipped_integracao_meli" | "no_template" | "no_phone";
  wamid?: string | null; error?: unknown; cause_at?: string | null; at?: string; manual?: boolean; by?: string | null;
};
type Delivery = { status: string; at: string; error: string | null } | null;
type Lead = {
  id: string; created_at: string; nome: string | null; telefone: string | null; email: string | null;
  portal: string | null; interesse: string | null;
  execution_id: string | null;  // execução `portal` no core: carga do e-mail, run da IA, disparo
  dispatch: Dispatch | null;    // o disparo gravado na própria linha do lead (waba)
  delivery: Delivery;           // último status da Meta pro wamid do template (sent/delivered/read/failed)
  resend_allowed: boolean;      // tem telefone
};

/** Erro legível: detalhe estruturado da Meta ({code, title, …}) ou string. */
function errText(e: unknown): string {
  if (!e) return "";
  if (typeof e === "string") return e;
  const o = e as { code?: number | string; title?: string; message?: string; detail?: unknown; error?: unknown };
  if (o.detail) return errText(o.detail);
  if (o.error && typeof o.error === "object") return errText(o.error);
  return `${o.code ?? ""} ${o.title ?? o.message ?? ""}`.trim() || JSON.stringify(e);
}

type Tone = "ok" | "danger" | "warn" | "muted";
/** Badge da linha: o disparo (waba) + o status da Meta, na ordem em que acontecem. */
function badgeOf(l: Lead): { label: string; tone: Tone; title?: string } {
  const d = l.dispatch;
  if (!d || d.status === "no_template" || d.status === "no_phone") return { label: "Sem disparo", tone: "muted" };
  if (d.status === "failed") return { label: "Falhou", tone: "danger", title: errText(d.error) };
  if (d.status === "skipped_conversa_ativa") return { label: `Conversa ativa${d.cause_at ? " " + spTime(d.cause_at) : ""}`, tone: "warn", title: "Lead escreveu dentro do intervalo configurado: template não enviado" };
  if (d.status === "skipped_timeout") return { label: `Timeout${d.cause_at ? " " + spTime(d.cause_at) : ""}`, tone: "warn", title: "Outro disparo de portal pro mesmo telefone dentro do intervalo configurado" };
  if (d.status === "skipped_integracao_olx" || d.status === "skipped_integracao_meli") return { label: `Bloqueado: ${d.status.endsWith("olx") ? "OLX" : "Mercado Livre"} conectado`, tone: "muted", title: "O lead chega pela integração direta da conexão: sem lead nem template automático (Enviar manda à mão)" };
  const dv = l.delivery;
  if (dv?.status === "failed") return { label: "Falhou na entrega", tone: "danger", title: dv.error ?? "failed" };
  if (dv?.status === "read") return { label: `Lido ${spTime(dv.at)}`, tone: "ok" };
  if (dv?.status === "delivered") return { label: `Entregue ${spTime(dv.at)}`, tone: "ok" };
  return { label: `Enviado${d.at ? " " + spTime(d.at) : ""}`, tone: "ok" };
}
const wasSent = (l: Lead) => l.dispatch?.status === "sent" && l.delivery?.status !== "failed";

const spTime = (iso: string) =>
  new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z").toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
const parse = (raw: string | null) => { try { return raw ? JSON.parse(raw) : {}; } catch { return {}; } };

const labelStyle: React.CSSProperties = { fontSize: 12, color: "var(--ink-secondary)", display: "block", marginBottom: 6 };
const inputStyle: React.CSSProperties = {
  padding: "8px 12px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8,
  color: "var(--ink)", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box",
};
const cardStyle: React.CSSProperties = { border: "1px solid var(--line)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 };
const h2Style: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: "var(--ink)", margin: 0 };
const muted: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 };
const TONE: Record<Tone, { color: string; line: string }> = {
  ok: { color: "var(--ok)", line: "var(--ok)" }, danger: { color: "var(--danger)", line: "var(--danger)" },
  warn: { color: "var(--warn)", line: "var(--warn)" }, muted: { color: "var(--ink-muted)", line: "var(--line)" },
};
const badge = (tone: Tone): React.CSSProperties => ({
  fontSize: 11, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap", background: "var(--surface-input)",
  color: TONE[tone].color, border: `1px solid ${TONE[tone].line}`,
});
const smallBtn: React.CSSProperties = {
  fontSize: 11, padding: "2px 8px", borderRadius: 6, cursor: "pointer", border: "1px solid var(--line-strong)",
  background: "transparent", color: "var(--ink)", whiteSpace: "nowrap",
};

export function ConfigPortais({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const p = agent.portais ?? {};
  const [enabled, setEnabled] = useState<boolean>(!!p.enabled);
  const [tpl, setTpl] = useState<Tpl | null>(p.template?.name ? p.template : null);
  const [cooldown, setCooldown] = useState<number>(Number(p.cooldown_minutes) || 0);
  // Flags do /send de template (o template SEMPRE entra no contexto da IA): contexto extra
  // que o cliente não vê, reabrir follow-up, remover trava humana.
  const [extraOn, setExtraOn] = useState<boolean>(!!p.context_extra_enabled);
  const [extra, setExtra] = useState<string>(typeof p.context_extra === "string" ? p.context_extra : "");
  const [resetFollow, setResetFollow] = useState<boolean>(!!p.reseta_follow);
  const [iaProcessa, setIaProcessa] = useState<boolean>(!!p.ia_processa);
  const [sendersGlobal, setSendersGlobal] = useState<boolean>(p.senders_global ?? true);
  const [senders, setSenders] = useState<Sender[]>(Array.isArray(p.senders) ? p.senders : []);
  const current = JSON.stringify({ enabled, tpl, cooldown, extraOn, extra, resetFollow, iaProcessa, sendersGlobal, senders });
  const [savedSnap, setSavedSnap] = useState(current);
  const setSender = (i: number, patch: Partial<Sender>) =>
    setSenders(senders.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const { data: bound } = useQuery<any | null>({
    queryKey: ["waba-binding", agent.id],
    queryFn: () => wabaApi.get(`/bindings/agent/${agent.id}`),
  });
  const connId: string | undefined = bound?.id;
  const { data: wabaTemplates = [] } = useQuery<any[]>({
    queryKey: ["waba-templates", connId],
    queryFn: () => wabaApi.get(`/connections/${connId}/templates`),
    enabled: !!connId,
  });
  const approved = wabaTemplates.filter((t) => t.status === "APPROVED");
  const { data: emails } = useQuery<{ logs: any[] }>({
    queryKey: ["portais-emails", connId],
    queryFn: () => wabaApi.get(`/connections/${connId}/logs?channel=email&limit=10`),
    enabled: !!connId, refetchInterval: 60_000,
  });
  const { data: leads } = useQuery<{ leads: Lead[] }>({
    queryKey: ["portais-leads", connId],
    queryFn: () => wabaApi.get(`/connections/${connId}/portais/leads?limit=50`),
    enabled: !!connId, refetchInterval: 60_000,
  });

  const mut = useMutation({
    mutationFn: () => api.patch(`/agents/${agent.id}`, {
      portais: {
        enabled, template: tpl?.name ? tpl : null, cooldown_minutes: Math.max(0, Math.floor(cooldown || 0)),
        context_extra_enabled: extraOn, context_extra: extra, reseta_follow: resetFollow, ia_processa: iaProcessa,
        senders_global: sendersGlobal,
        senders: senders.filter((s) => s.name.trim() && s.match.trim()).map((s) => ({ name: s.name.trim(), match: s.match.trim().toLowerCase() })),
        emails: Array.isArray(p.emails) ? p.emails : [],
      },
    }),
    onSuccess: () => {
      setSavedSnap(JSON.stringify({ enabled, tpl, cooldown, extraOn, extra, resetFollow, iaProcessa, sendersGlobal, senders }));
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
    },
  });
  useUnsavedGuard(current !== savedSnap, mut.mutateAsync);

  // Enviar/Reenviar: o core renderiza o template ATUAL e a WABA manda sem cooldown.
  const [confirmLead, setConfirmLead] = useState<Lead | null>(null);
  const resend = useMutation({
    // 90 s: api → waba → Jinja → waba → Meta; o 15 s padrão abortava com o template já enviado.
    mutationFn: (id: string) => api.post<{ dispatch: Dispatch }>(`/agents/${agent.id}/portais/leads/${id}/resend`, {}, 90_000),
    onSuccess: (r) => {
      setConfirmLead(null);
      qc.invalidateQueries({ queryKey: ["portais-leads", connId] });
      if (r?.dispatch?.status === "failed") toastError(`Meta recusou: ${errText(r.dispatch.error)}`);
    },
    onError: (e: any) => { setConfirmLead(null); toastError(errText(e?.payload?.detail ?? e?.payload) || e?.message || "Falha ao enviar."); },
  });
  function askSend(l: Lead) {
    if (wasSent(l)) setConfirmLead(l);  // já saiu: confirma antes de mandar de novo
    else resend.mutate(l.id);
  }

  const selected = approved.find((t) => t.name === tpl?.name && t.language === tpl?.language)
    ?? approved.find((t) => t.name === tpl?.name);
  const body = bodyOf(selected);
  const slots = selected ? countSlots(body) : (tpl?.params.length ?? 0);
  function pick(v: string) {
    const t = approved.find((x) => `${x.name}|${x.language}` === v);
    setTpl(t ? { name: t.name, language: t.language, params: Array.from({ length: countSlots(bodyOf(t)) }, () => "") } : null);
  }
  function setParam(i: number, v: string) {
    if (!tpl) return;
    const params = Array.from({ length: Math.max(tpl.params.length, i + 1) }, (_, j) => (j === i ? v : tpl.params[j] ?? ""));
    setTpl({ ...tpl, params });
  }

  const leadCount = leads?.leads.length ?? 0;
  const sentCount = leads?.leads.filter((l) => l.dispatch?.status === "sent").length ?? 0;

  // "Ver execução": a execução `portal` do core (step email = carga, llm = run da IA com a
  // resposta da tool/tokens/custo, template = disparo e status), no mesmo modal das execuções.
  const [execOpen, setExecOpen] = useState<ExecRow | null>(null);
  async function openExecution(id: string) {
    try {
      setExecOpen(await api.get<ExecRow>(`/agents/${agent.id}/executions/${id}`));
    } catch (e: any) {
      toastError(e?.payload?.detail ?? "Execução não encontrada.");
    }
  }
  if (execOpen) {
    return (
      <ExecModal exec={execOpen} stepsUrl={`/agents/${agent.id}/executions/${execOpen.id}/steps`}
        onClose={() => setExecOpen(null)} />
    );
  }

  return (
    <div style={{ maxWidth: 820, display: "flex", flexDirection: "column", gap: 16 }}>
      {confirmLead && (
        <div {...backdropClose(() => setConfirmLead(null))} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setConfirmLead(null)} role="alertdialog" label="Reenviar template" style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, maxWidth: 420, width: "90%" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 10 }}>Mandar de novo?</div>
            <div style={{ fontSize: 13, color: "var(--ink-secondary)", margin: "0 0 20px" }}>
              O template já foi {badgeOf(confirmLead).label.toLowerCase()} para {confirmLead.nome || confirmLead.telefone}
              {confirmLead.telefone && confirmLead.nome ? ` (${confirmLead.telefone})` : ""}. Reenviar manda o template
              atual do agente outra vez, agora.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => resend.mutate(confirmLead.id)} disabled={resend.isPending}
                style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                {resend.isPending ? "Enviando..." : "Reenviar"}
              </button>
              <button onClick={() => setConfirmLead(null)} disabled={resend.isPending}
                style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}>
                Cancelar
              </button>
            </div>
          </DialogPanel>
        </div>
      )}
      <div style={muted}>
        Leads que os portais mandam por e-mail para o endereço da conexão WhatsApp deste agente (card da conexão →
        E-mail). O core extrai o comprador e, com o template abaixo, a WABA cria o lead e chama ele na hora. A
        resposta dele no WhatsApp cai no atendimento normal.
      </div>
      {!connId && <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>Agente sem conexão WhatsApp vinculada (aba Conexão).</div>}

      {connId && (
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <h2 style={h2Style}>Últimos e-mails recebidos{bound?.email_address ? ` · ${bound.email_address}` : ""}</h2>
            <Link to="/waba" search={{ open: connId, log: "email" }} style={{ fontSize: 12, color: "var(--brand)" }}>
              Ver logs completos
            </Link>
          </div>
          {!bound?.email_address && <span style={muted}>A conexão ainda não tem e-mail: crie em WABA → card da conexão → E-mail.</span>}
          {emails && emails.logs.length === 0 && <span style={muted}>Nenhum e-mail nos últimos 30 dias.</span>}
          {emails?.logs.map((l) => {
            const pl = parse(l.payload);
            return (
              <div key={l.id} style={{ display: "grid", gridTemplateColumns: "92px 1fr", gap: 8, fontSize: 12, alignItems: "baseline" }}>
                <span style={{ color: "var(--ink-muted)" }}>{spTime(l.created_at)}</span>
                <span style={{ color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <b style={{ fontWeight: 600 }}>{l.contact_name || pl.from || "—"}</b> · {pl.subject || "(sem assunto)"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {connId && (
        <div style={cardStyle}>
          <h2 style={h2Style}>Leads criados · {leadCount} nos últimos 30 dias, {sentCount} com disparo</h2>
          {leads && leads.leads.length === 0 && <span style={muted}>Nenhum lead extraído ainda. Os remetentes dos portais são cadastrados no painel Dev → Portais por e-mail.</span>}
          {leads?.leads.map((l) => {
            const b = badgeOf(l);
            const sending = resend.isPending && resend.variables === l.id;
            return (
              <div key={l.id} style={{ display: "grid", gridTemplateColumns: "92px 1fr auto auto auto", gap: 8, fontSize: 12, alignItems: "center" }}>
                <span style={{ color: "var(--ink-muted)" }}>{spTime(l.created_at)}</span>
                <span style={{ color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <b style={{ fontWeight: 600 }}>{l.nome || l.telefone || l.email}</b>
                  {l.telefone && l.nome ? ` · ${l.telefone}` : ""}{l.portal ? ` · ${l.portal}` : ""}{l.interesse ? ` · ${l.interesse}` : ""}
                </span>
                <span style={badge(b.tone)} title={b.title}>{b.label}</span>
                {l.resend_allowed ? (
                  <button onClick={() => askSend(l)} disabled={sending} style={smallBtn}
                    title={wasSent(l) ? "Manda o template atual de novo (pede confirmação)" : "Manda o template atual do agente pra este lead, ignorando o intervalo"}>
                    {sending ? "Enviando..." : wasSent(l) || b.tone === "danger" ? "Reenviar" : "Enviar"}
                  </button>
                ) : <span />}
                {l.execution_id ? (
                  <button onClick={() => openExecution(l.execution_id!)} style={smallBtn}>Ver execução</button>
                ) : <span />}
              </div>
            );
          })}
          <span style={muted}>
            Vermelho = a Meta recusou ou não entregou (passe o mouse para ver o erro). Amarelo = o template não saiu
            por causa do intervalo abaixo. Enviar/Reenviar usa o template atual do agente, sem intervalo. O log completo
            do disparo (template, resposta da Meta) está em "Ver execução".
          </span>
        </div>
      )}

      <div style={cardStyle}>
        <h2 style={h2Style}>Remetentes aceitos</h2>
        <span style={muted}>Só e-mail de remetente aceito vira lead (extração com IA, execução "Portal (e-mail)"). Os globais são cadastrados em Dev → Portais por e-mail; aqui você liga/desliga os globais e acrescenta os deste agente (e-mail exato ou domínio).</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" id="portais-senders-global" checked={sendersGlobal} onChange={(e) => setSendersGlobal(e.target.checked)}
            style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
          <label htmlFor="portais-senders-global" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
            Usar os remetentes globais (Dev)
          </label>
        </div>
        {senders.map((s, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr auto", gap: 8, alignItems: "center" }}>
            <input style={inputStyle} placeholder="Nome (ex.: Só Carrão)" value={s.name} onChange={(e) => setSender(i, { name: e.target.value })} />
            <input style={inputStyle} placeholder="e-mail ou domínio" value={s.match} onChange={(e) => setSender(i, { match: e.target.value })} />
            <button onClick={() => setSenders(senders.filter((_, j) => j !== i))}
              style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", border: "1px solid var(--line-strong)", background: "transparent", color: "var(--danger)" }}>
              Remover
            </button>
          </div>
        ))}
        <button onClick={() => setSenders([...senders, { name: "", match: "" }])}
          style={{ alignSelf: "flex-start", padding: "7px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", border: "1px solid var(--line-strong)", background: "transparent", color: "var(--ink)" }}>
          + Remetente deste agente
        </button>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" id="portais-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
          <label htmlFor="portais-enabled" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
            Chamar o lead por template assim que o e-mail do portal chegar
          </label>
        </div>
        <span style={muted}>
          O lead nunca escreveu (janela fechada): só template aprovado da conta WhatsApp. Cada {"{{N}}"} aceita texto e
          variáveis: {"{{ portais.lead.nome }}"}, {"{{ portais.lead.telefone }}"}, {"{{ portais.lead.email }}"},{" "}
          {"{{ portais.lead.interesse }}"}, {"{{ portais.lead.ad_url }}"}, {"{{ portais.lead.resumo }}"}, {"{{ portais.lead.source }}"}, {"{{ portais.assunto }}"}, {"{{ portais.remetente }}"}. Variável vazia = não envia (fica no log).
        </span>
        {!connId ? (
          <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Vincule uma conexão WhatsApp para escolher o template.</span>
        ) : (
          <>
            <div>
              <label style={labelStyle}>Template aprovado</label>
              <select style={inputStyle} value={tpl?.name ? `${tpl.name}|${tpl.language}` : ""} onChange={(e) => pick(e.target.value)}>
                <option value="">— nenhum —</option>
                {approved.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>)}
              </select>
            </div>
            {body && (
              <div style={{ fontSize: 12, color: "var(--ink-secondary)", whiteSpace: "pre-wrap", padding: "8px 10px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8 }}>{body}</div>
            )}
            {tpl && Array.from({ length: slots }, (_, i) => (
              <ExpressionEditor key={i} autoGrow minHeight={22} maxHeight={78}
                headerLeft={<span style={{ fontSize: 11, color: "var(--ink-secondary)", fontFamily: "var(--font-mono)" }}>{`{{${i + 1}}}`}</span>}
                value={tpl.params[i] ?? ""} onChange={(v) => setParam(i, v)} />
            ))}
          </>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4, borderTop: "1px solid var(--line)" }}>
          <span style={muted}>
            O template enviado entra sempre no contexto da IA como fala dela (texto real, variáveis preenchidas), então
            ela continua a conversa quando o lead responder. Abaixo, o que mais o disparo faz:
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="checkbox" id="portais-extra" checked={extraOn} onChange={(e) => setExtraOn(e.target.checked)}
              style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
            <label htmlFor="portais-extra" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
              Injetar contexto extra na IA (o lead não vê)
            </label>
          </div>
          {extraOn && (
            <>
              <ExpressionEditor autoGrow minHeight={44} maxHeight={160}
                headerLeft={<span style={{ fontSize: 11, color: "var(--ink-secondary)" }}>[Mensagem do sistema]</span>}
                value={extra} onChange={setExtra} />
              <span style={muted}>
                Entra no mesmo balão do template, como bloco "[Mensagem do sistema]". Aceita as variáveis {"{{ portais.* }}"} acima
                e as variáveis do sistema (globais, agente). Ex.: "Lead do {"{{ portais.source }}"}: {"{{ portais.lead.resumo }}"}".
              </span>
            </>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="checkbox" id="portais-reset-follow" checked={resetFollow} onChange={(e) => setResetFollow(e.target.checked)}
              style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
            <label htmlFor="portais-reset-follow" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
              Ligar o follow-up (reabre e zera a contagem a partir do disparo)
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="checkbox" id="portais-ia-processa" checked={iaProcessa} onChange={(e) => setIaProcessa(e.target.checked)}
              style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
            <label htmlFor="portais-ia-processa" style={{ fontSize: 13, color: "var(--ink)", cursor: "pointer" }}>
              Desbloquear travas de atendimento humano
            </label>
          </div>
          {iaProcessa && (
            <span style={{ ...muted, color: "var(--warn)" }}>
              Lead que está em atendimento humano recebe o template e a IA volta a responder nele. Avise o vendedor.
            </span>
          )}
        </div>
        <div>
          <label style={labelStyle} htmlFor="portais-cooldown">Não disparar se houve conversa ou disparo recente</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input id="portais-cooldown" type="number" min={0} step={1} value={cooldown}
              onChange={(e) => setCooldown(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              style={{ ...inputStyle, width: 110 }} />
            <span style={{ fontSize: 13, color: "var(--ink)" }}>minutos</span>
          </div>
          <span style={muted}>
            Ao chegar um lead, a conexão confere se esse telefone escreveu no WhatsApp ou recebeu outro disparo de portal
            nos últimos N minutos; se sim, o template não sai e a linha fica amarela (Conversa ativa / Timeout).
            0 = desligado (sempre dispara). O botão Enviar/Reenviar ignora este intervalo.
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => mut.mutate()} disabled={mut.isPending}
            style={{ background: "var(--brand)", color: "var(--surface-card)", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: "pointer" }}>
            {mut.isPending ? "Salvando..." : "Salvar"}
          </button>
          <SavedNote show={mut.isSuccess} />
        </div>
      </div>
    </div>
  );
}
